/**
 * Date utilities for period-related calculations.
 *
 * All period dates operate in Toronto timezone (America/Toronto).
 * See PRD: prds/dates-and-periods.md
 */
import { formatInTimeZone } from "date-fns-tz";
import { parseISO, format } from "date-fns";

/**
 * The canonical timezone for all period date calculations.
 * All period start_date / end_date values are interpreted in this timezone.
 * (R03: may eventually be stored per-period; for now it's hardcoded.)
 */
export const PERIOD_TIMEZONE = "America/Toronto";

/**
 * Returns today's date as a 'YYYY-MM-DD' string in the period timezone.
 * Used for all period status comparisons — both client-side and as
 * the reference "today" for badge logic.
 *
 * String comparison with DB date strings (also 'YYYY-MM-DD') is safe
 * because ISO date format is lexicographically sortable.
 */
export function todayInPeriodTimezone(): string {
  return formatInTimeZone(new Date(), PERIOD_TIMEZONE, "yyyy-MM-dd");
}

/**
 * Parse a date-only string ('YYYY-MM-DD') into a Date at local midnight,
 * avoiding the UTC-midnight pitfall of `new Date("YYYY-MM-DD")`.
 *
 * Use this for **display purposes only** (formatting dates for the UI).
 * For period status comparisons, use todayInPeriodTimezone() + string comparison.
 *
 * Accepts both 'YYYY-MM-DD' date strings and full ISO timestamps (e.g. a
 * confirmedAt value like '2026-02-28T15:30:00Z'). date-fns parseISO handles
 * both: date-only strings are parsed as local midnight (no UTC-midnight shift),
 * and full timestamps are parsed with their embedded offset.
 */
export function parseLocalDate(dateStr: string): Date {
  return parseISO(dateStr);
}

/**
 * Format a date-only DB string ('YYYY-MM-DD') for display.
 * Parses safely (no UTC shift) then formats via date-fns.
 */
export function formatDateString(dateStr: string, fmt = "yyyy-MM-dd"): string {
  return format(parseLocalDate(dateStr), fmt);
}
